import { PowerOff } from "lucide-react";

interface FacilityDeactivatedPageProps {
  /**
   * Overrides the guidance line below the heading. Defaults to the copy
   * used for the inline case (a stale bookmark/typed URL to a facility
   * that's since been deactivated), which points at the Admin Dashboard —
   * appropriate there since that case can be hit by a platform_admin too.
   * The standalone /facility-deactivated landing page (SignInForm's
   * post-login redirect for a platform_user with no active facility at
   * all — platform_admins never land there, they always go to
   * /admin/dashboard) passes a simpler message instead, since that
   * audience has no Admin Dashboard access to "check".
   */
  message?: string;
}

const DEFAULT_MESSAGE =
  "Its data is no longer available here. Check the admin dashboard, or contact the admin user for more details.";

// Shown in place of a facility-scoped page's content when the backend
// returns its dedicated 403 for a deactivated facility (see
// isFacilityDeactivatedError in src/lib/apiError.ts) — most commonly hit via
// a stale bookmark or a typed URL to a facility that's since been
// deactivated, since nothing in the app links to one anymore once it's
// deactivated. Deliberately distinct from NotFoundPage's "404" copy: the
// facility isn't missing, it's just no longer active.
export default function FacilityDeactivatedPage({
  message = DEFAULT_MESSAGE,
}: FacilityDeactivatedPageProps) {
  return (
    <div className="flex h-screen flex-col items-center justify-center text-center px-4">
      <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-3 bg-[var(--custom-slate-25)] border border-[var(--custom-slate-50)]">
        <PowerOff size={22} className="text-[var(--custom-slate-500)]" />
      </div>
      <h2 className="text-lg font-semibold text-gray-900 mb-1">
        This facility has been deactivated
      </h2>
      <p className="text-sm text-gray-500 max-w-sm">{message}</p>
    </div>
  );
}
